/**
 * CreateVisitModal — Modal-Formular zum Anlegen eines Vor-Ort-Termins.
 *
 * Felder:
 *   - Kunde (Combobox, sucht über /customers)
 *   - Datum (Mantine DatePickerInput)
 *   - Teilnehmer (MultiSelect über bekannte Mitarbeiter)
 *   - Verknüpfte Kampagnen (MultiSelect, optional)
 *   - Kurzprotokoll (Textarea, optional — nur für durchgeführte Termine)
 *
 * Statt eines Datums geht auch „in Klärung“ mit Frist (#351): Der Termin ist
 * angefragt, aber nicht vereinbart.
 */
import {
  Alert,
  Button,
  Combobox,
  Group,
  InputBase,
  Loader,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  useCombobox,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCustomer, useCustomers } from "../../hooks/useCustomers.js";
import { useCreateVisit } from "../../hooks/useVisits.js";
import { toLocalDateString } from "../../lib/date.js";
import { ClarifyDeadlineInput } from "./ClarifyDeadlineInput.js";

// ── Zod Schema ───────────────────────────────────────────────────────────────

// Schema dient hier als Single Source of Truth für den Formulartyp (z.infer unten).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const createVisitSchema = z.object({
  customerId: z.string().min(1, "Bitte einen Kunden auswählen"),
  segmentId: z.string().min(1, "Bitte ein Segment auswählen"),
  mode: z.enum(["fixed", "clarifying"]),
  scheduledAt: z.date({ required_error: "Bitte ein Datum wählen" }),
  clarifyUntil: z.date().nullable(),
  attendees: z.array(z.string()),
  campaignIds: z.array(z.string()),
  minutes: z.string(),
});

type CreateVisitFormValues = z.infer<typeof createVisitSchema>;

// ── Mock-Daten für Mitarbeiter (wird in Phase 2 durch GET /users ersetzt) ────

interface EmployeeOption {
  value: string;
  label: string;
}

interface CampaignOption {
  value: string;
  label: string;
}

// ── Props ────────────────────────────────────────────────────────────────────

interface CreateVisitModalProps {
  opened: boolean;
  onClose: () => void;
  /** Pre-fill Kunde (z. B. aus Kundenseite heraus) */
  prefilledCustomerId?: string;
  prefilledCustomerName?: string;
  /** Pre-fill Segment (z. B. aus einer Erinnerung pro (Kunde × Segment)) */
  prefilledSegmentId?: string;
  /** Verfügbare Mitarbeiter für Teilnehmer-Auswahl */
  employees?: EmployeeOption[];
  /** Verfügbare Kampagnen für Verknüpfung */
  campaigns?: CampaignOption[];
}

// ── Kunden-Combobox ──────────────────────────────────────────────────────────

interface CustomerComboboxProps {
  value: string;
  valueLabel: string;
  onChange: (id: string, name: string) => void;
  error?: string;
}

function CustomerCombobox({ value, valueLabel, onChange, error }: CustomerComboboxProps) {
  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [searchInput, setSearchInput] = useState(valueLabel);
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  const { data, isLoading } = useCustomers({
    q: debouncedSearch || undefined,
    page: 1,
  });

  const options = (data?.items ?? []).map((c) => (
    <Combobox.Option key={c.id} value={c.id}>
      {c.name}
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        const customer = data?.items.find((c) => c.id === val);
        if (customer) {
          onChange(customer.id, customer.name);
          setSearchInput(customer.name);
        }
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        {/* Kein natives `required`: die Browser-Validierung ("Please fill out
            this field.") würde vor form.onSubmit greifen und englisch sein —
            die deutschen Fehlertexte kommen aus form.validate. */}
        <InputBase
          label={t("pages.visits.createModal.customer")}
          withAsterisk
          placeholder={t("pages.visits.createModal.customerPlaceholder")}
          value={searchInput}
          error={error}
          onChange={(e) => {
            setSearchInput(e.currentTarget.value);
            if (e.currentTarget.value !== valueLabel) {
              onChange("", "");
            }
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            if (!value) setSearchInput("");
          }}
          rightSection={isLoading ? <Loader size={16} /> : <Combobox.Chevron />}
          rightSectionPointerEvents="none"
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>
          {isLoading ? (
            <Combobox.Empty>{t("pages.visits.createModal.customerSearchSearching")}</Combobox.Empty>
          ) : options.length === 0 ? (
            <Combobox.Empty>{t("pages.visits.createModal.customerSearchEmpty")}</Combobox.Empty>
          ) : (
            options
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}

// ── CreateVisitModal ─────────────────────────────────────────────────────────

export function CreateVisitModal({
  opened,
  onClose,
  prefilledCustomerId,
  prefilledCustomerName,
  prefilledSegmentId,
  employees = [],
  campaigns = [],
}: CreateVisitModalProps) {
  const { t } = useTranslation();
  const createVisit = useCreateVisit();

  const [customerName, setCustomerName] = useState(prefilledCustomerName ?? "");

  const form = useForm<CreateVisitFormValues>({
    initialValues: {
      customerId: prefilledCustomerId ?? "",
      segmentId: prefilledSegmentId ?? "",
      mode: "fixed",
      scheduledAt: new Date(),
      clarifyUntil: null,
      attendees: [],
      campaignIds: [],
      minutes: "",
    },
    validate: {
      customerId: (val) => (!val ? t("pages.visits.createModal.customerError") : null),
      segmentId: (val) => (!val ? t("pages.visits.createModal.segmentError") : null),
      scheduledAt: (val, values) =>
        values.mode === "fixed" && !val ? t("pages.visits.createModal.dateError") : null,
      clarifyUntil: (val, values) =>
        values.mode !== "clarifying"
          ? null
          : !val
            ? t("pages.visits.clarify.deadlineError")
            : toLocalDateString(val) < toLocalDateString(new Date())
              ? t("pages.visits.clarify.deadlinePastError")
              : null,
    },
  });

  // Wenn das Modal mit einer neuen Vorbelegung wieder geöffnet wird (z.B.
  // einmal aus dem Reminder-Tab, dann nochmal mit anderem Kunden), die
  // Initialwerte synchronisieren.
  useEffect(() => {
    if (!opened) return;
    form.setValues({
      customerId: prefilledCustomerId ?? "",
      segmentId: prefilledSegmentId ?? "",
      mode: "fixed",
      scheduledAt: new Date(),
      clarifyUntil: null,
      attendees: [],
      campaignIds: [],
      minutes: "",
    });
    setCustomerName(prefilledCustomerName ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, prefilledCustomerId, prefilledSegmentId, prefilledCustomerName]);

  // Segmente des gewählten Kunden laden (T11 — Termin pro (Kunde × Segment)).
  // Hinweis: useCustomer triggert erst, wenn eine customerId vorliegt (enabled-Flag).
  const customerQuery = useCustomer(form.values.customerId);
  const segmentOptions = (customerQuery.data?.segments ?? []).map((s) => ({
    value: s.id,
    label: s.name,
  }));

  // Auto-Auswahl: wenn Kunde nur ein Segment hat, dieses sofort setzen.
  useEffect(() => {
    if (!form.values.customerId) return;
    if (form.values.segmentId) return;
    if (segmentOptions.length === 1) {
      form.setFieldValue("segmentId", segmentOptions[0]!.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.customerId, segmentOptions.length]);

  function handleClose() {
    form.reset();
    setCustomerName(prefilledCustomerName ?? "");
    onClose();
  }

  async function handleSubmit(values: CreateVisitFormValues) {
    // toLocalDateString statt toISOString (Befund B1) — sonst rutscht das
    // lokal zur Mitternacht gewählte Datum in MESZ/MEZ auf den Vortag.
    const inKlaerung = values.mode === "clarifying" && values.clarifyUntil !== null;
    const payload = {
      customerId: values.customerId,
      segmentId: values.segmentId,
      ...(inKlaerung
        ? { clarifyUntil: toLocalDateString(values.clarifyUntil!) }
        : { scheduledAt: toLocalDateString(values.scheduledAt) }),
      attendeeIds: values.attendees,
      linkedCampaignIds: values.campaignIds.length > 0 ? values.campaignIds : undefined,
      // Ein Protokoll gibt es erst, wenn der Termin stattgefunden hat.
      shortMinutes: inKlaerung ? undefined : values.minutes.trim() || undefined,
    };

    try {
      await createVisit.mutateAsync(payload);
      handleClose();
    } catch {
      // Error anzeigen — wird durch createVisit.error abgefragt
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("pages.visits.createModal.title")}
      size="md"
      closeOnClickOutside={false}
    >
      <form onSubmit={form.onSubmit((values) => void handleSubmit(values))}>
        <Stack gap="md">
          {/* Kunde */}
          {prefilledCustomerId ? (
            <InputBase
              label={t("pages.visits.createModal.customer")}
              value={prefilledCustomerName ?? prefilledCustomerId}
              readOnly
              disabled
            />
          ) : (
            <CustomerCombobox
              value={form.values.customerId}
              valueLabel={customerName}
              error={form.errors.customerId as string | undefined}
              onChange={(id, name) => {
                form.setFieldValue("customerId", id);
                // Bei Kundenwechsel die Segmentwahl zurücksetzen.
                form.setFieldValue("segmentId", "");
                setCustomerName(name);
              }}
            />
          )}

          {/* Segment (Pflicht ab T11) */}
          <Select
            label={t("pages.visits.createModal.segment")}
            description={
              segmentOptions.length === 0 && form.values.customerId
                ? t("pages.visits.createModal.segmentNoneHint")
                : t("pages.visits.createModal.segmentHint")
            }
            placeholder={
              !form.values.customerId
                ? t("pages.visits.createModal.segmentPickCustomerFirst")
                : customerQuery.isLoading
                ? t("pages.visits.createModal.segmentLoading")
                : t("pages.visits.createModal.segmentPlaceholder")
            }
            withAsterisk
            data={segmentOptions}
            value={form.values.segmentId || null}
            onChange={(val) => form.setFieldValue("segmentId", val ?? "")}
            error={form.errors.segmentId as string | undefined}
            disabled={
              !form.values.customerId ||
              customerQuery.isLoading ||
              segmentOptions.length === 0
            }
            searchable
          />

          {/* Fester Termin oder „in Klärung“ (#351) */}
          <Stack gap={4}>
            <SegmentedControl
              fullWidth
              value={form.values.mode}
              onChange={(val) => form.setFieldValue("mode", val as "fixed" | "clarifying")}
              data={[
                { value: "fixed", label: t("pages.visits.clarify.modeFixed") },
                { value: "clarifying", label: t("pages.visits.clarify.modeClarifying") },
              ]}
            />
            {form.values.mode === "clarifying" && (
              <Text size="xs" c="dimmed">
                {t("pages.visits.clarify.modeHint")}
              </Text>
            )}
          </Stack>

          {form.values.mode === "clarifying" && (
            <ClarifyDeadlineInput
              value={form.values.clarifyUntil}
              onChange={(val) => form.setFieldValue("clarifyUntil", val)}
              error={form.errors.clarifyUntil as string | undefined}
            />
          )}

          {/* Datum */}
          {form.values.mode === "fixed" && (
            <DatePickerInput
              label={t("pages.visits.createModal.date")}
              withAsterisk
              placeholder={t("pages.visits.createModal.datePlaceholder")}
              value={form.values.scheduledAt}
              onChange={(val) => form.setFieldValue("scheduledAt", val ?? new Date())}
              error={form.errors.scheduledAt as string | undefined}
              // Ein Datum vor heute wird als durchgeführt gespeichert (#249) —
              // das steht hier, bevor jemand speichert, nicht erst danach.
              description={
                toLocalDateString(form.values.scheduledAt) < toLocalDateString(new Date())
                  ? t("pages.visits.createModal.datePastHint")
                  : undefined
              }
              locale="de"
            />
          )}

          {/* Teilnehmer */}
          <MultiSelect
            label={t("pages.visits.createModal.attendees")}
            placeholder={t("pages.visits.createModal.attendeesPlaceholder")}
            data={employees}
            value={form.values.attendees}
            onChange={(val) => form.setFieldValue("attendees", val)}
            searchable
            clearable
          />

          {/* Verknüpfte Kampagnen */}
          <MultiSelect
            label={t("pages.visits.createModal.campaigns")}
            placeholder={t("pages.visits.createModal.campaignsPlaceholder")}
            data={campaigns}
            value={form.values.campaignIds}
            onChange={(val) => form.setFieldValue("campaignIds", val)}
            searchable
            clearable
          />

          {/* Kurzprotokoll */}
          {form.values.mode === "fixed" && (
            <Textarea
              label={t("pages.visits.createModal.shortMinutes")}
              description={t("pages.visits.createModal.shortMinutesDescription")}
              placeholder={t("pages.visits.createModal.shortMinutesPlaceholder")}
              value={form.values.minutes}
              onChange={(e) => form.setFieldValue("minutes", e.currentTarget.value)}
              minRows={3}
              autosize
            />
          )}

          {/* Fehler-Anzeige */}
          {createVisit.isError && (
            <Alert color="red" title={t("pages.visits.createModal.saveErrorTitle")}>
              {createVisit.error instanceof Error
                ? createVisit.error.message
                : t("pages.visits.createModal.error")}
            </Alert>
          )}

          {/* Aktionen */}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={handleClose} disabled={createVisit.isPending}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={createVisit.isPending}>
              {form.values.mode === "clarifying"
                ? t("pages.visits.clarify.submitClarifying")
                : t("pages.visits.createModal.submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
